import React, { useLayoutEffect, useRef, useState } from 'react';
import { resolveActiveSearcherId, visibleSearchers } from 'shared/searchers';
import { HERO_TITLE_FALLBACK } from '../../../templateCodes';
import { HeroTemplateProps } from '../../types';
import { resolveConfig, resolveSearcherTabs } from '../../resolveConfig';
import HeroSearchCard from '../../HeroSearchCard';
import HeroSearcherTabs from '../../HeroSearcherTabs';
import { manifest } from './manifest';
import '../../Hero.css';
import '../HeroA/HeroA.css';
import './HeroReserve.css';
import { CONTACT_ICONS, SOCIAL_ICONS, DefaultSocialIcon } from '../../../header/icons';
import type { SocialNetwork } from '../../../header/types';

const PhoneIcon = CONTACT_ICONS.phone;
const EmailIcon = CONTACT_ICONS.email;

function ContactStrip({
  contactStrip,
}: {
  contactStrip: NonNullable<HeroTemplateProps['contactStrip']>;
}) {
  const color = contactStrip.contactColor || '#d1d4d6';
  const socials = (contactStrip.socialLinks || []).filter((s) => s.show && s.url);

  const hasContact =
    (contactStrip.showPhone && contactStrip.phone) ||
    (contactStrip.showEmail && contactStrip.email);

  if (!hasContact && socials.length === 0) {
    return null;
  }

  return (
    <div className='hero-contact-strip' style={{ '--contact-color': color } as React.CSSProperties}>
      <div className='hero-contact-strip__inner'>
        {hasContact ? (
          <div className='hero-contact-strip__pill'>
            <span className='hero-contact-strip__label'>Contactanos</span>
            <div className='hero-contact-strip__items'>
              {contactStrip.showPhone && contactStrip.phone ? (
                <a
                  className='hero-contact-strip__item'
                  href={`tel:${contactStrip.phone.replace(/[^+\d]/g, '')}`}
                >
                  <PhoneIcon />
                  <span>{contactStrip.phone}</span>
                </a>
              ) : null}
              {contactStrip.showEmail && contactStrip.email ? (
                <a className='hero-contact-strip__item' href={`mailto:${contactStrip.email}`}>
                  <EmailIcon />
                  <span>{contactStrip.email}</span>
                </a>
              ) : null}
            </div>
          </div>
        ) : null}
        {socials.length > 0 ? (
          <div className='hero-contact-strip__pill hero-contact-strip__pill--social'>
            <span className='hero-contact-strip__label'>Seguinos en nuestras redes!</span>
            <div className='hero-contact-strip__items hero-contact-strip__items--social'>
              {socials.map((social, index) => {
                const Icon =
                  SOCIAL_ICONS[social.network.toLowerCase() as SocialNetwork] ?? DefaultSocialIcon;
                return (
                  <a
                    className='hero-contact-strip__social-icon'
                    href={social.url}
                    key={`${social.network}-${index}`}
                    rel='noreferrer noopener'
                    target='_blank'
                    title={social.network}
                  >
                    <Icon />
                  </a>
                );
              })}
            </div>
          </div>
        ) : null}
      </div>
    </div>
  );
}

const HeroReserve: React.FC<HeroTemplateProps> = ({
  config: partialConfig,
  contactStrip,
  onSearcherSelect,
  renderSearchForm,
  mobileSearchOpen = false,
  paisId,
}) => {
  const config = resolveConfig(manifest, partialConfig);
  const searcherTabs = resolveSearcherTabs(manifest, config);
  const searchers = visibleSearchers(searcherTabs);
  const activeSearcherId = resolveActiveSearcherId(searcherTabs, config.activeSearcherTabId);
  const activeSearcher = searchers.find((searcher) => searcher.id === activeSearcherId);
  const showSearchers =
    config.showSearchers !== false && searchers.length > 0 && Boolean(activeSearcherId);
  const displayTitle = (config.title || '').trim() || HERO_TITLE_FALLBACK;
  const displayDescription = (config.description || '').trim();
  const align = config.noSearcherTextAlign || 'left';
  const mobileTabsRef = useRef<HTMLDivElement>(null);
  const heroRef = useRef<HTMLElement>(null);
  const searchModuleRef = useRef<HTMLDivElement>(null);
  const [mobileSearchSpacerPx, setMobileSearchSpacerPx] = useState(0);

  const mobileImageUrl = (config.mobileBackgroundImageUrl || '').trim();
  const desktopImageUrl = (config.backgroundImageUrl || '').trim();
  const rootClassName = [
    'hero',
    'hero--a',
    'hero--reserve',
    !config.showCopy && 'hero--no-copy',
    !showSearchers && 'hero--no-searchers',
    `hero--align-${align}`,
    mobileSearchOpen && 'hero--mobile-search-open',
  ]
    .filter(Boolean)
    .join(' ');

  const rootStyle = {
    '--hero-title-color': config.titleColor,
    '--hero-description-color': config.descriptionColor,
    '--hero-brand': config.brandColor,
    '--hero-brand-2': config.brandColor2,
    '--hero-overlay-opacity': config.overlay / 100,
    '--hero-bg-image': desktopImageUrl ? `url(${desktopImageUrl})` : 'none',
    ...(mobileImageUrl ? { '--hero-mobile-bg-image': `url(${mobileImageUrl})` } : {}),
  } as React.CSSProperties;

  // Prototipo v18: overlay at top 50% of hero; spacer reserves overflow so the contact
  // strip (sibling below) is pushed under the open searchers instead of being covered.
  useLayoutEffect(() => {
    if (!mobileSearchOpen || !showSearchers) {
      setMobileSearchSpacerPx(0);
      return undefined;
    }

    const hero = heroRef.current;
    const module = searchModuleRef.current;
    if (!hero || !module) return undefined;

    const updateSpacer = () => {
      const heroHeight = hero.offsetHeight;
      const moduleHeight = module.offsetHeight;
      const overflow = Math.max(0, heroHeight / 2 + moduleHeight - heroHeight);
      setMobileSearchSpacerPx(Math.ceil(overflow));
    };

    updateSpacer();

    if (typeof ResizeObserver === 'undefined') return undefined;
    const observer = new ResizeObserver(updateSpacer);
    observer.observe(hero);
    observer.observe(module);
    return () => observer.disconnect();
  }, [mobileSearchOpen, showSearchers, activeSearcherId]);

  const scrollMobileSearchersNext = () => {
    const nav = mobileTabsRef.current;
    if (!nav) return;
    const amount = Math.max(180, nav.clientWidth * 0.65);
    const atEnd = nav.scrollLeft + nav.clientWidth >= nav.scrollWidth - 8;
    nav.scrollTo({ left: atEnd ? 0 : nav.scrollLeft + amount, behavior: 'smooth' });
  };

  return (
    <>
      <section className={rootClassName} ref={heroRef} style={rootStyle}>
        <div className='hero__content'>
          {config.showCopy && (
            <div className='hero__copy'>
              <h1>{displayTitle}</h1>
              {displayDescription ? <p>{displayDescription}</p> : null}
            </div>
          )}

          {showSearchers ? (
            <div className='hero__search-module' id='hero-search-module' ref={searchModuleRef}>
              <div className='hero__mobile-searcher-row'>
                <HeroSearcherTabs
                  activeSearcherId={activeSearcherId}
                  onSearcherSelect={onSearcherSelect}
                  ref={mobileTabsRef}
                  searchers={searchers}
                />
                {searchers.length > 1 ? (
                  <button
                    aria-label='Ver más buscadores'
                    className='hero__mobile-searcher-next'
                    onClick={scrollMobileSearchersNext}
                    type='button'
                  >
                    <svg fill='none' height='14' viewBox='0 0 8 14' width='8'>
                      <path
                        d='M1 1l6 6-6 6'
                        stroke='currentColor'
                        strokeLinecap='round'
                        strokeLinejoin='round'
                        strokeWidth='2'
                      />
                    </svg>
                  </button>
                ) : null}
              </div>
              {/* No title / benefits — same as desktop ReserveSearchPanel (prototipo v18). */}
              <HeroSearchCard
                activeSearcherId={activeSearcherId}
                activeSearcherLabel=''
                benefits={[]}
                paisId={paisId}
                renderSearchForm={renderSearchForm}
                showTitle={false}
                title=''
              />
            </div>
          ) : null}
        </div>
      </section>
      <div
        aria-hidden='true'
        className='hero-mobile-search-spacer'
        style={{ height: mobileSearchSpacerPx }}
      />
      {contactStrip ? <ContactStrip contactStrip={contactStrip} /> : null}
    </>
  );
};

export default HeroReserve;
